import { InputGroup, InputGroupDropDown } from "@certn/cascadia-react";
import { useJsonForms } from "@jsonforms/react";
import { useContext } from "react";
import { useTranslation } from "react-i18next";

import { CascadiaJsonFormContext } from "../CascadiaJsonFormContext";
import { SummaryItem } from "../components/SummaryItem";
import {
  CascadiaControlProps,
  withCascadiaControlProps,
} from "../wrappers/withCascadiaControlProps";

interface IncomeTypes {
  FULL_TIME_EMPLOYMENT: string;
  PART_TIME_EMPLOYMENT: string;
  SELF_EMPLOYED: string;
  STUDENT_LOANS: string;
  PENSION: string;
  DISABILITY: string;
  OTHER: string;
  NO_INCOME: string;
}
type IncomeTypeKeys = keyof IncomeTypes;

export function CurrencyAndIncomeAmountControl(props: CascadiaControlProps) {
  const context = useJsonForms();
  const currencies = props.schema?.properties?.currency?.oneOf || [];
  const { isSummary } = useContext(CascadiaJsonFormContext);
  const { t } = useTranslation();

  const incomeType: IncomeTypeKeys = context?.core?.data.income_type;
  const mappingForLabelType: { [key in IncomeTypeKeys]: string } = {
    FULL_TIME_EMPLOYMENT: t("Currency and income amount"),
    PART_TIME_EMPLOYMENT: t("Currency and income amount"),
    SELF_EMPLOYED: t("Annual gross salary"),
    STUDENT_LOANS: t("Funding per year"),
    PENSION: t("Gross income from pensions per year"),
    DISABILITY: t("Income per year"),
    OTHER: t("Gross income per year"),
    NO_INCOME: t("NA"),
  };

  const currencyAndAmountLabel =
    mappingForLabelType[incomeType] || t("Currency and amount");

  const data = props.data || { amount: "", currency: "CAD" };

  if (isSummary) {
    const currencyAndOrAmount = [data.currency, data.amount]
      .filter(Boolean)
      .join(" ");
    return (
      <SummaryItem label={props.label}>
        {String(currencyAndOrAmount || t("None provided"))}
      </SummaryItem>
    );
  }

  return (
    <InputGroup
      errors={props.errors}
      isDisabled={!props.enabled}
      isInvalid={props.invalid}
      isRequired={props.required}
      label={currencyAndAmountLabel}
      name={props.path}
      onBlur={props.setTouched}
      onChange={(value) => {
        props.setValue({ ...data, amount: parseFloat(value) });
      }}
      type="number"
      value={data.amount}
      dropdown={
        <InputGroupDropDown
          items={currencies.map((item) => ({
            value: item.const,
            display: item.const,
          }))}
          selectedKey={data.currency}
          onSelectionChange={(value) => {
            props.setValue({
              ...data,
              currency: value,
            });
          }}
        />
      }
    />
  );
}

CurrencyAndIncomeAmountControl.displayName = "CurrencyAndIncomeAmountControl";

export const CurrencyAndIncomeAmountControlRenderer = withCascadiaControlProps(
  CurrencyAndIncomeAmountControl
);
